import { useEffect, useState } from "react";
import api from "../lib/api";

/**
 * useLiveMode — shared hook that exposes the current connector state.
 *
 *   { mode, isLive, isConnected, hasLiveData, loading }
 *
 * - `isLive`        : connector mode is "live"
 * - `isConnected`   : a Google Ads OAuth profile exists AND mode is live
 *                     (i.e. the connector is wired up, regardless of whether
 *                     any data has synced yet)
 * - `hasLiveData`   : at least one document with a non-seed source exists
 *                     somewhere in campaigns/leads/landing_pages/alerts
 *
 * Empty-state policy used by pages:
 *   - !isLive                          → MOCK mode, show seed data normally
 *   - isLive && !isConnected           → no connector configured → "Connect a data source" CTA
 *   - isLive && isConnected && !hasLiveData
 *                                       → connector live but nothing synced YET → connector CTA
 *                                          (unlikely now that we sync on Test, but kept for safety)
 *   - isLive && isConnected && hasLiveData (per-module count = 0)
 *                                       → MODULE-SPECIFIC empty state ("No alerts yet" etc.)
 *                                          NOT a connector CTA
 */
let _cache = { data: null, ts: 0 };
const CACHE_MS = 10_000;

export default function useLiveMode() {
  const [state, setState] = useState(_cache.data || {
    mode: null, isLive: false, isConnected: false, hasLiveData: false,
  });
  const [loading, setLoading] = useState(_cache.data === null);

  useEffect(() => {
    let cancelled = false;
    const now = Date.now();
    if (_cache.data !== null && now - _cache.ts < CACHE_MS) {
      setState(_cache.data);
      setLoading(false);
      return;
    }
    api
      .get("/connectors/google-ads/status")
      .then((r) => {
        if (cancelled) return;
        const m = r.data?.mode || "mock";
        const next = {
          mode: m,
          isLive: m === "live",
          isConnected: !!r.data?.is_connected,
          hasLiveData: !!r.data?.has_live_data,
        };
        _cache = { data: next, ts: Date.now() };
        setState(next);
      })
      .catch(() => {
        if (!cancelled) setState({ mode: "mock", isLive: false, isConnected: false, hasLiveData: false });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return { ...state, loading };
}
